import React, { useEffect, useRef, useState } from "react";
import { CustomTable, InputControl, Loader, SelectChip } from "../../../shared";
import { useRecipeList } from "../../Recipe/hooks";
import { Stack, Typography, Checkbox, Chip, Box } from "@mui/material";
import ArrowRight from "../../../assets/images/Icons/arrow_right.png";
import LeftArrow from "../../../assets/images/Icons/left_arrow.png";
import { useImmer } from "use-immer";
import CustomDialogFooter from "../../../shared/components/CustomDialogFooter";

export default function AssignAndUnassignDialog({
  handleCancel,
  assignRecipeToStore,
  handleAssignRecipeToStore,
  state,
  setState,
  storeMappedRecipe,
  storeMappedAllRecipe,
}) {
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const [scrollPosition, setScrollPosition] = useImmer({
    scrollLeft: null,
    width: null,
  });
  const {
    recipe,
    onSearchHandler,
    pagination,
    listQuery,
    selectChipCategory,
    tableData,
  } = useRecipeList({
    recipelist: true,
    isStoreRecipe: true,
    setState,
    state,
  });

  // Create allRecipeList: recipes in recipe.recipeList but not in state.recipeList
  const isDialogOpenData = state?.isDialogOpen
    ? storeMappedAllRecipe?.data?.data
    : storeMappedRecipe?.data?.data;

  const stateRecipeIds = isDialogOpenData?.map((r) => r.id);
  const allRecipeList = recipe?.recipeList
    ?.map((category) => {
      const updatedSubRows = category?.subRows?.filter(
        (subRow) => !stateRecipeIds.includes(subRow.id)
      );

      return {
        ...category,
        subRows: updatedSubRows,
      };
    })
    .filter((category) => category.subRows.length > 0);

  const ref = useRef();

  // State for managing selected recipes
  const handleScroll = () => {
    setScrollPosition((draft) => {
      draft.scrollLeft = ref.current.scrollLeft;
      draft.width = ref.current.clientWidth;
    });
  };

  useEffect(() => {
    if (ref.current) {
      ref.current.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (ref.current) {
        ref.current.addEventListener("scroll", handleScroll);
      }
    };
  }, [ref?.current]);

  const onScrollRefClick = (direction) => {
    const width = direction === "left" ? -250 : 250;
    ref.current.scrollLeft += width;
  };

  // Get all recipe IDs (only leaf nodes, not category headers)
  const getAllRecipeIds = () => {
    const recipeIds = [];
    const traverse = (data) => {
      data.forEach((item) => {
        if (item.subRows && item.subRows.length > 0) {
          traverse(item.subRows);
        } else {
          recipeIds.push(item.id);
        }
      });
    };
    if (recipe.recipeList) {
      traverse(recipe.recipeList);
    }
    return recipeIds;
  };

  // Handle individual recipe selection
  const handleRecipeSelect = (recipeId) => {
    setSelectedRecipes((prev) => {
      if (prev.includes(recipeId)) {
        return prev.filter((id) => id !== recipeId);
      } else {
        return [...prev, recipeId];
      }
    });
  };

  // Handle select all recipes
  const handleSelectAll = () => {
    const allRecipeIds = getAllRecipeIds();
    if (selectedRecipes.length === allRecipeIds.length) {
      // If all are selected, unselect all
      setSelectedRecipes([]);
    } else {
      // Select all
      setSelectedRecipes(allRecipeIds);
    }
  };

  // Check if all recipes are selected
  const isAllSelected = () => {
    const allRecipeIds = getAllRecipeIds();
    return (
      allRecipeIds.length > 0 &&
      selectedRecipes &&
      selectedRecipes.length === allRecipeIds.length
    );
  };

  // Check if some recipes are selected
  const isIndeterminate = () => {
    const allRecipeIds = getAllRecipeIds();
    return (
      selectedRecipes &&
      selectedRecipes.length > 0 &&
      selectedRecipes.length < allRecipeIds.length
    );
  };

  const columns = [
    {
      accessorKey: "recipe_name",
      header: () => "Products",
      cell: (props) => {
        const { row } = props;
        return !row.depth ? (
          <Typography
            sx={{
              color: "primary.main",
              textTransform: "uppercase",
            }}
          >
            {row.original.recipe_name}
          </Typography>
        ) : (
          <Stack direction="row" gap="12px" alignItems="center">
            <span className="link-text">{row.original.recipe_name}</span>
          </Stack>
        );
      },
    },

    {
      id: "status",
      accessorKey: "status",
      header: "Status",
      cell: (props) => {
        const { row } = props;
        return row.depth ? (
          row.original.status ? (
            <Chip
              size="small"
              color="primary"
              variant="filled"
              label="ACTIVE"
            />
          ) : (
            <Chip size="small" color="gray" label="INACTIVE" />
          )
        ) : (
          ""
        );
      },
    },
    {
      id: "actions",
      header: "",
      meta: { width: "50px" },
      cell: (props) => {
        const { row } = props;
        // Only show checkbox for recipe rows (not category headers)
        if (!row.depth) {
          return null;
        }

        return (
          <InputControl
            type="checkbox"
            sx={{ alignSelf: "end" }}
            control={
              <Checkbox
                edge="end"
                checked={
                  selectedRecipes && selectedRecipes.includes(row.original.id)
                }
                onChange={() => handleRecipeSelect(row.original.id)}
              />
            }
          />
        );
      },
    },
  ];

  return (
    <>
      <CustomTable
        pagination={pagination}
        searchText={recipe?.storeRecipeSearch ?? null}
        manualFiltering={true}
        defaultData={allRecipeList ?? []}
        columns={columns}
        headerText="Assign Recipes"
        subRow="subRows"
        isPagination={false}
        disablePagination={true}
        allRowsExpanded={true}
        isLoading={recipe.isLoading}
        onSearchChange={onSearchHandler}
        customRowStyle={(row) => {
          return !row.depth ? { borderBottom: "none" } : null;
        }}
        tableHeaderStyles={{
          position: "sticky",
          top: "0px",
          background: "#fff",
          zIndex: 99,
        }}
        tableStyle={{ overflow: "unset", height: "calc(90vh - 68px)" }}
        tableHeaderContent={
          <Stack
            direction={"row"}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {ref?.current?.scrollLeft > 0 ? (
              <Box
                component={"img"}
                height={20}
                width={20}
                src={LeftArrow}
                onClick={() => onScrollRefClick("left")}
                sx={{ cursor: "pointer" }}
              />
            ) : null}
            {recipe?.storeRecipeSearch && (
              <Stack
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  borderRadius: "32px",
                  gap: "8px",
                  border: "1px solid #DFE8F2",
                  p: "6px 8px 6px 10px",
                  bgcolor: "#1760D2",
                  color: "#fff",
                  minWidth: "fit-content",
                }}
              >
                <Typography variant="subtitle2" fontWeight={500} noWrap>
                  {recipe?.storeRecipeSearch}
                </Typography>
                <Chip
                  size="small"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    bgcolor: "#124da8",
                    color: "#fff",
                  }}
                  label={listQuery?.data?.length}
                />
              </Stack>
            )}

            <Stack
              id="scroll-ele"
              ref={ref}
              sx={{
                p: "6px",
                flexDirection: "row",
                gap: "6px",
                overflow: "auto",
                scrollBehavior: "smooth",
              }}
              className="scroll-hide"
            >
              {!selectChipCategory.isLoading &&
              selectChipCategory.chipCategory &&
              selectChipCategory.chipCategory.chipCategoryOptions &&
              Array.isArray(
                selectChipCategory.chipCategory.chipCategoryOptions
              ) ? (
                selectChipCategory.chipCategory.chipCategoryOptions.map(
                  (v, i) => {
                    const chipCategoryId =
                      tableData.recipeSelectedCategory.chipCategoryId === 0 ||
                      !tableData.recipeSelectedCategory.chipCategoryId
                        ? selectChipCategory?.chipCategory
                            ?.chipCategoryOptions?.[0]?.id
                        : tableData.recipeSelectedCategory.chipCategoryId;
                    const isSelected = v.id === chipCategoryId;
                    return (
                      <Stack
                        sx={{
                          flexDirection: "row",
                          cursor: "pointer",
                        }}
                        onClick={(e) => {
                          !recipe?.storeRecipeSearch &&
                            selectChipCategory.chipCategoryValuesHandler(v);
                        }}
                      >
                        <SelectChip
                          key={i}
                          value={v}
                          isSelected={isSelected}
                          countKey="recipeListCount"
                        />
                      </Stack>
                    );
                  }
                )
              ) : (
                <Loader />
              )}
            </Stack>

            {ref?.current?.scrollWidth - ref?.current?.scrollLeft >
            ref?.current?.clientWidth ? (
              <Box
                component={"img"}
                height={20}
                width={20}
                src={ArrowRight}
                onClick={() => onScrollRefClick("right")}
                sx={{ cursor: "pointer" }}
              />
            ) : null}
          </Stack>
        }
        customTopRow={
          allRecipeList.length && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography fontWeight={500} fontSize="16px">
                Select All Recipes
              </Typography>
              <InputControl
                type="checkbox"
                control={
                  <Checkbox
                    edge="end"
                    checked={isAllSelected()}
                    indeterminate={isIndeterminate()}
                    onChange={handleSelectAll}
                  />
                }
              />
            </Stack>
          )
        }
        headerContentStyle={{
          position: "sticky",
          top: "50px",
          background: "#fff",
          zIndex: 99,
        }}
      />

      <CustomDialogFooter
        disabled={
          selectedRecipes.length === 0 || assignRecipeToStore?.isPending
        }
        handleClose={() => handleCancel(false)}
        onSubmit={() => handleAssignRecipeToStore(selectedRecipes)}
        submitButton="Assign"
      />
    </>
  );
}
